import { FolderTypeEnum } from "../src/taskpane/components/enums/FolderTypeEnum";
import { axiosRetryGet, axiosRetryPatch } from "./AxiosRetryUtil";
import { logger } from "./logger";

export enum CategoryDisplayNames {
    Teams = "Saved to Teams",
    SharePoint = "Saved to SharePoint",
    OneDrive = "Saved to OneDrive",
}

export interface IMailSettingsDto {
    accountId: string,
    encryptedPayload: string,
}

export class OutlookUtils {

    private static readonly savedToTeamsCategory: Office.CategoryDetails = {
        "displayName": CategoryDisplayNames.Teams,
        "color": "Preset8" //Purple
    };

    private static readonly savedToSharePointCategory: Office.CategoryDetails = {
        "displayName": CategoryDisplayNames.SharePoint,
        "color": "Preset11" //Steel
    };

    private static readonly savedToOneDriveCategory: Office.CategoryDetails = {
        "displayName": CategoryDisplayNames.OneDrive,
        "color": "Preset7" //Blue
    };

    static masterCategoriesToAdd: Office.CategoryDetails[] = [
        OutlookUtils.savedToTeamsCategory,
        OutlookUtils.savedToSharePointCategory,
        OutlookUtils.savedToOneDriveCategory
    ];

    public static getOutlookToken = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, result => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value);
                } else {
                    reject(result.error);
                }
            });
        });
    }

    public static getRestURL = async (): Promise<string> => {
        let restURL: string;
        try {
            if (Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                // In Windows, Mac, and the web client, this indicates a shared item so use SharedProperties properties to construct the REST URL.
                // Add-ins don't activate on shared items in mobile so no need to handle.
                restURL = await new Promise<string>((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject("getSharedPropertiesAsync timeout");
                    }, 1500);
                    Office.context.mailbox.item.getSharedPropertiesAsync((result) => {
                        clearTimeout(timeout);
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            const sharedProperties: Office.SharedProperties = result.value;
                            resolve(sharedProperties.targetRestUrl + "/v2.0/users/" + sharedProperties.targetMailbox);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getRestURL)");
        }

        if (!restURL) {
            // restURL = Office.context.mailbox.restUrl + "/v2.0/me";
            restURL = Office.context.mailbox.restUrl + "/v2.0/users/" + Office.context.mailbox.userProfile.emailAddress;
        }

        return restURL;
    }

    public static getEWSToken = (): Promise<string> => {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
            return new Promise(resolve => resolve(null));
        }

        return new Promise((resolve, reject) => {
            Office.context.mailbox.getCallbackTokenAsync(result => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value);
                } else {
                    reject(result.error);
                }
            });
        });
    }

    public static getEWSURL = (): string => {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
            return null;
        }
        return Office.context.mailbox.ewsUrl;
    }

    private static getMasterCategories = async (): Promise<Office.CategoryDetails[]> => {
        try {
            return await new Promise<Office.CategoryDetails[]>((resolve, reject) => {
                Office.context.mailbox.masterCategories.getAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(result.error);
                    }
                });
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getMasterCategories)");
            return null;
        }
    }

    private static addMasterCategories = async (categories: Office.CategoryDetails[]): Promise<boolean> => {
        try {
            // Api not supported for shared items.
            if (!Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                return await new Promise<boolean>((resolve, reject) => {
                    Office.context.mailbox.masterCategories.addAsync(categories, null, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            resolve(true);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (addMasterCategories)");
        }
        return false;
    }

    private static ensureMasterCategoryExists = async (category: Office.CategoryDetails): Promise<boolean> => {
        const currentCategories: Office.CategoryDetails[] = await OutlookUtils.getMasterCategories();
        const exists: boolean = currentCategories?.some(c => c.displayName?.trim().toLowerCase() === category.displayName?.trim().toLowerCase());
        if (exists === true || (await OutlookUtils.addMasterCategories([category]))) {
            return true;
        }

        return false;
    }

    //You can apply a category to an item by assigning the displayName property of the category to the categories collection of the item.
    //https://docs.microsoft.com/en-us/graph/api/resources/outlookcategory?view=graph-rest-1.0
    //FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel
    public static applyCategory = async (itemId: string, categoryType: FolderTypeEnum) => {
        try {
            const token = await OutlookUtils.getOutlookToken();
            const selectors = "/?$select=categories"
            const getMessageUrl = await OutlookUtils.getRestURL() + "/messages/" + itemId + selectors;
            const headers = {
                "Authorization": "Bearer " + token
            }
            let categories: string[];
            try {
                categories = (await axiosRetryGet(getMessageUrl, {
                    headers: headers
                }))?.data.Categories;
            } catch (e) {
                logger(e, "OutlookUtils.ts (applyCategory 1)");
            }

            if ((categories.includes(CategoryDisplayNames.Teams) && categoryType === FolderTypeEnum.teamChannel)
                || (categories.includes(CategoryDisplayNames.SharePoint) && categoryType === FolderTypeEnum.sharepoint)) {
                return; //no need for update or going further if category is already included 
            }

            switch (categoryType) {
                case FolderTypeEnum.teamChannel:
                    categories.push(CategoryDisplayNames.Teams);
                    break;
                case FolderTypeEnum.sharepoint:
                    categories.push(CategoryDisplayNames.SharePoint);
                    break;
                case FolderTypeEnum.oneDrive:
                    categories.push(CategoryDisplayNames.OneDrive);
                    break;
                default:
                    break;
            }

            const updateMessageUrl = await OutlookUtils.getRestURL() + "/messages/" + itemId;
            const data = {
                Categories: categories
            };
            try {
                const result = (await axiosRetryPatch(updateMessageUrl, data, {
                    headers: {
                        ...headers,
                        'Content-Type': 'application/json', //required: probably json?
                    }
                }))?.data;
            } catch (e) {
                logger(e, "OutlookUtils.ts (applyCategory 2)");
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (applyCategory 3)");
        }
    }

    //categoryType: FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel
    public static getCategoriesToAssign = async (itemId: string, categoryType: FolderTypeEnum): Promise<string[]> => {

        if (Office.context.requirements.isSetSupported("Mailbox", "1.8")) {
            try {
                const token = await OutlookUtils.getOutlookToken();
                const selectors = "/?$select=categories"
                const getMessageUrl = await OutlookUtils.getRestURL() + "/messages/" + itemId + selectors;
                const headers = {
                    "Authorization": "Bearer " + token
                }
                let categories: string[];
                try {
                    categories = (await axiosRetryGet(getMessageUrl, {
                        headers: headers
                    }))?.data.Categories;
                } catch (e) {
                    logger(e, "OutlookUtils.ts (getCategoriesToAssign 1)");
                }

                if (categories) {
                    if (
                        (categories.some(c => c.trim().toLowerCase() === CategoryDisplayNames.Teams.toLowerCase()) && categoryType === FolderTypeEnum.teamChannel)
                        || (categories.some(c => c.trim().toLowerCase() === CategoryDisplayNames.SharePoint.toLowerCase()) && categoryType === FolderTypeEnum.sharepoint)
                        || (categories.some(c => c.trim().toLowerCase() === CategoryDisplayNames.OneDrive.toLowerCase()) && categoryType === FolderTypeEnum.oneDrive)
                    ) {
                        return null;
                    }
                }
                else {
                    categories = [];
                }

                let categoryToSet: Office.CategoryDetails;
                switch (categoryType) {
                    case FolderTypeEnum.teamChannel:
                        categoryToSet = OutlookUtils.savedToTeamsCategory;
                        break;
                    case FolderTypeEnum.sharepoint:
                        categoryToSet = OutlookUtils.savedToSharePointCategory;
                        break;
                    case FolderTypeEnum.oneDrive:
                        categoryToSet = OutlookUtils.savedToOneDriveCategory;
                        break;
                    default:
                        break;
                }

                if (categoryToSet && await OutlookUtils.ensureMasterCategoryExists(categoryToSet)) {
                    categories.push(categoryToSet.displayName);
                    return categories;
                }
            } catch (error) {
                logger(error, "OutlookUtils.ts (getCategoriesToAssign 2)");
            }
        }

        return null;
    }

    public static setEWSItemCategory = (ewsItemId: string, categoriesToAssign: string[]) => {
        // makeEwsRequestAsync api not supported for shared items.
        if (categoriesToAssign?.length > 0 && ewsItemId && !Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
            const ewsCategorySoapRequest: string = `<?xml version="1.0" encoding="utf-8"?>
            <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages" xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
              <soap:Header>
                <t:RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0" />
              </soap:Header>
              <soap:Body>
                <UpdateItem MessageDisposition="SaveOnly" ConflictResolution="AlwaysOverwrite" xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">
                  <ItemChanges>
                    <t:ItemChange>
                      <t:ItemId Id="${ewsItemId}" />
                      <t:Updates>
                        <t:SetItemField>
                          <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray" />
                          <t:Message>
                            <t:ExtendedProperty>
                              <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray" />
                              <t:Values>
                                ${categoriesToAssign.map(category => `<t:Value>${category}</t:Value>`).join()}
                              </t:Values>
                            </t:ExtendedProperty>
                          </t:Message>
                        </t:SetItemField>
                      </t:Updates>
                    </t:ItemChange>
                  </ItemChanges>
                </UpdateItem>
              </soap:Body>
            </soap:Envelope>`;
            Office.context.mailbox?.makeEwsRequestAsync(ewsCategorySoapRequest, asyncRes => console.log(asyncRes));
        }
    }
}